import React from "react"

// Simple utility to combine classes
const cn = (...inputs: (string | undefined | null | false)[]): string => {
  return inputs.filter(Boolean).join(" ")
}

export interface ToolTableProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export interface ToolTableHeaderProps
  extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode
}

export interface ToolTableBodyProps
  extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode
}

export interface ToolTableRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode
  variant?: "default" | "totals"
}

export interface ToolTableHeadProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode
}

export interface ToolTableCellProps
  extends React.TdHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode
}

const ToolTable = React.forwardRef<HTMLDivElement, ToolTableProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        className={cn("component-ui-tool-table", className)}
        ref={ref}
        {...props}
      >
        <div className="tool-table-wrapper">
          <table className="tool-table">{children}</table>
        </div>
      </div>
    )
  },
)

ToolTable.displayName = "ToolTable"

const ToolTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  ToolTableHeaderProps
>(({ className, children, ...props }, ref) => {
  return (
    <thead className={cn("tool-table-header", className)} ref={ref} {...props}>
      {children}
    </thead>
  )
})

ToolTableHeader.displayName = "ToolTableHeader"

const ToolTableBody = React.forwardRef<
  HTMLTableSectionElement,
  ToolTableBodyProps
>(({ className, children, ...props }, ref) => {
  return (
    <tbody className={cn("tool-table-body", className)} ref={ref} {...props}>
      {children}
    </tbody>
  )
})

ToolTableBody.displayName = "ToolTableBody"

const ToolTableRow = React.forwardRef<HTMLTableRowElement, ToolTableRowProps>(
  ({ className, variant = "default", children, ...props }, ref) => {
    return (
      <tr
        className={cn(
          "tool-table-row",
          variant === "totals" && "tool-table-row-totals",
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
      </tr>
    )
  },
)

ToolTableRow.displayName = "ToolTableRow"

const ToolTableHead = React.forwardRef<
  HTMLTableCellElement,
  ToolTableHeadProps
>(({ className, children, ...props }, ref) => {
  return (
    <th className={cn("tool-table-head", className)} ref={ref} {...props}>
      {children}
    </th>
  )
})

ToolTableHead.displayName = "ToolTableHead"

const ToolTableCell = React.forwardRef<
  HTMLTableCellElement,
  ToolTableCellProps
>(({ className, children, ...props }, ref) => {
  return (
    <td className={cn("tool-table-cell", className)} ref={ref} {...props}>
      {children}
    </td>
  )
})

ToolTableCell.displayName = "ToolTableCell"

export {
  ToolTable,
  ToolTableHeader,
  ToolTableBody,
  ToolTableRow,
  ToolTableHead,
  ToolTableCell,
}
